import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  FamilyRestroom as ParentIcon,
  School as SchoolIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { parentService, studentService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { DataTable } from '../../components/Common';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import { showToast } from '../../utils/toast';

const ParentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [addressValue, setAddressValue] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    const fetchParentInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await parentService.getById(id);
        
        if (response.success && response.data) {
          setParentData(response.data);
        } else if (response.data) {
          setParentData(response.data);
        } else {
          setError('Parent not found');
        }
      } catch (err) {
        console.error('Error fetching parent details:', err);
        setError(err.message || 'Failed to fetch parent details');
      } finally {
        setLoading(false);
      }
    };

    const fetchChildren = async () => {
      try {
        setLoadingChildren(true);
        const response = await studentService.getAll({ user_id: id });
        
        if (response.success && Array.isArray(response.data)) {
          setChildren(response.data);
        } else if (Array.isArray(response)) {
          setChildren(response);
        }
      } catch (err) {
        console.error('Error fetching children:', err);
      } finally {
        setLoadingChildren(false);
      }
    };

    if (id) {
      fetchParentInfo();
      fetchChildren();
    }
  }, [id]);

  const handleBack = () => {
    window.history.back();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditAddress = () => {
    setAddressValue(parentData.address || '');
    setEditAddressOpen(true);
  };

  const handleCloseEditAddress = () => {
    setEditAddressOpen(false);
    setAddressValue('');
  };

  const handleSaveAddress = async () => {
    try {
      setSavingAddress(true);
      const response = await parentService.update(id, {
        address: addressValue,
      });
      
      if (response.success || response.data) {
        showToast.success('Address updated successfully');
        handleCloseEditAddress();
        // Refetch parent data to get latest information
        const refreshResponse = await parentService.getById(id);
        if (refreshResponse.success && refreshResponse.data) {
          setParentData(refreshResponse.data);
        } else if (refreshResponse.data) {
          setParentData(refreshResponse.data);
        }
      } else {
        showToast.error(response.error || 'Failed to update address');
      }
    } catch (err) {
      console.error('Error updating address:', err);
      showToast.error(err.response?.data?.error || err.message || 'Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleEditPhone = () => {
    setPhoneValue(parentData.phone || '');
    setEditPhoneOpen(true);
  };

  const handleCloseEditPhone = () => {
    setEditPhoneOpen(false);
    setPhoneValue('');
  };

  const handleSavePhone = async () => {
    // Validate phone number (should start with 9-6 and be 10 digits)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (phoneValue && !phoneRegex.test(phoneValue)) {
      showToast.error('Phone number must start with 6-9 and be 10 digits');
      return;
    }

    try {
      setSavingPhone(true);
      const response = await parentService.update(id, {
        phone: phoneValue,
      });
      
      if (response.success || response.data) {
        showToast.success('Phone number updated successfully');
        handleCloseEditPhone();
        // Refetch parent data to get latest information
        const refreshResponse = await parentService.getById(id);
        if (refreshResponse.success && refreshResponse.data) {
          setParentData(refreshResponse.data);
        } else if (refreshResponse.data) {
          setParentData(refreshResponse.data);
        }
      } else {
        showToast.error(response.error || 'Failed to update phone number');
      }
    } catch (err) {
      console.error('Error updating phone:', err);
      showToast.error(err.response?.data?.error || err.message || 'Failed to update phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleEditEmail = () => {
    setEmailValue(parentData.email || '');
    setEditEmailOpen(true);
  };

  const handleCloseEditEmail = () => {
    setEditEmailOpen(false);
    setEmailValue('');
  };

  const handleSaveEmail = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailValue && !emailRegex.test(emailValue)) {
      showToast.error('Please enter a valid email address');
      return;
    }

    try {
      setSavingEmail(true);
      const response = await parentService.update(id, {
        email: emailValue,
      });
      
      if (response.success || response.data) {
        showToast.success('Email updated successfully');
        handleCloseEditEmail();
        // Refetch parent data to get latest information
        const refreshResponse = await parentService.getById(id);
        if (refreshResponse.success && refreshResponse.data) {
          setParentData(refreshResponse.data);
        } else if (refreshResponse.data) {
          setParentData(refreshResponse.data);
        }
      } else {
        showToast.error(response.error || 'Failed to update email');
      }
    } catch (err) {
      console.error('Error updating email:', err);
      showToast.error(err.response?.data?.error || err.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2.5, md: 3 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!parentData) {
    return null;
  }

  // Prepare parent details for table
  const parentDetailsData = [
    { id: 1, field: 'Email', value: parentData.email || '—' },
    { id: 2, field: 'Phone', value: parentData.phone || '—' },
    { id: 3, field: 'Address', value: parentData.address || '—' },
    { id: 4, field: 'Date of Joining', value: formatDate(parentData.date_of_joining) || '—' },
    { id: 5, field: 'Status', value: parentData.is_active ? 'Active' : 'Inactive' },
  ];

  const parentDetailsColumns = [
    {
      header: 'Field',
      field: 'field',
      render: (value) => (
        <Typography sx={{ fontWeight: 600, minWidth: 120 }}>
          {value}
        </Typography>
      ),
    },
    {
      header: 'Details',
      field: 'value',
      render: (value, row) => {
        if (row.field === 'Address' || row.field === 'Phone' || row.field === 'Email') {
          const handleEdit = () => {
            if (row.field === 'Address') {
              handleEditAddress();
            } else if (row.field === 'Phone') {
              handleEditPhone();
            } else if (row.field === 'Email') {
              handleEditEmail();
            }
          };

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ flex: 1 }}>{value}</Typography>
              <IconButton
                size="small"
                onClick={handleEdit}
                color="primary"
                sx={{ ml: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        return <Typography>{value}</Typography>;
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Header 
        onMenuClick={() => setSidebarOpen(true)} 
        showBackButton={true}
        onBack={handleBack}
        address={parentData?.address || null}
      />
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${0}px` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>

          {/* Basic Details Section */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              {/* Avatar */}
              <Avatar
                src={parentData.profile_image || null}
                alt={parentData.full_name}
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  bgcolor: getAvatarColor(parentData.full_name),
                  color: '#ffffff',
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(17, 82, 99, 0.3)',
                }}
              >
                {!parentData.profile_image && getInitials(parentData.full_name)}
              </Avatar>

              {/* Parent Info */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {parentData.full_name}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {parentData.email || '—'}
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Phone:</strong> {parentData.phone || '—'}
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Joined:</strong> {formatDate(parentData.date_of_joining)}
                  </Typography>
                  <Chip
                    label={parentData.is_active ? 'Active' : 'Inactive'}
                    color={parentData.is_active ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600, height: 22 }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Tabs Section */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 64,
                  fontSize: '0.9375rem',
                },
              }}
            >
              <Tab
                icon={<SchoolIcon />}
                iconPosition="start"
                label="Children"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label="Personal Details"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
            </Tabs>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Children Tab */}
              {activeTab === 0 && (
                <Box>
                  {loadingChildren ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : children.length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                      }}
                    >
                      {children.map((child) => (
                        <Card
                          key={child.id}
                          elevation={2}
                          sx={{
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                            },
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  bgcolor: getAvatarColor(child.full_name),
                                  color: '#ffffff',
                                  fontSize: '1.25rem',
                                  fontWeight: 700,
                                }}
                              >
                                {getInitials(child.full_name)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                  {child.full_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Roll No: {child.roll_number || '—'}
                                </Typography>
                              </Box>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography variant="body2">
                                <strong>Class:</strong> {child.class_name || '—'}
                                {child.class_section ? ` - ${child.class_section}` : ''}
                              </Typography>
                              <Typography variant="body2">
                                <strong>DOB:</strong> {formatDate(child.date_of_birth) || '—'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Admission:</strong> {formatDate(child.admission_date) || '—'}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={child.is_active ? 'Active' : 'Inactive'}
                                  color={child.is_active ? 'success' : 'default'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No children found for this parent.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Personal Details Tab */}
              {activeTab === 1 && (
                <Box>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent>
                      <DataTable
                        data={parentDetailsData}
                        columns={parentDetailsColumns}
                        emptyMessage="Parent details not available"
                        showActions={false}
                      />
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Edit Address Dialog */}
      <Dialog open={editAddressOpen} onClose={handleCloseEditAddress} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Address</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Address"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={addressValue}
            onChange={(e) => setAddressValue(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditAddress} disabled={savingAddress}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={savingAddress}
            startIcon={savingAddress ? <CircularProgress size={16} /> : null}
          >
            {savingAddress ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Phone Dialog */}
      <Dialog open={editPhoneOpen} onClose={handleCloseEditPhone} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Phone Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={phoneValue}
            onChange={(e) => setPhoneValue(e.target.value)}
            placeholder="Enter 10-digit phone number starting with 6-9"
            inputProps={{ maxLength: 10 }}
            sx={{ mt: 2 }}
            helperText="Phone number must start with 6-9 and be 10 digits"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPhone} disabled={savingPhone}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePhone}
            variant="contained"
            disabled={savingPhone}
            startIcon={savingPhone ? <CircularProgress size={16} /> : null}
          >
            {savingPhone ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={editEmailOpen} onClose={handleCloseEditEmail} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="Enter email address"
            sx={{ mt: 2 }}
            helperText="Enter a valid email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditEmail} disabled={savingEmail}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEmail}
            variant="contained"
            disabled={savingEmail}
            startIcon={savingEmail ? <CircularProgress size={16} /> : null}
          >
            {savingEmail ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentView;

